<template>
  <div :class="['surface-beethoven-preview', isMapPostPreview ? 'map-preview' : null]">
    <div v-if="!isLoaded" class="progress-container">
      <progress-indeterminate />
    </div>
    <a
      v-if="isLoaded && showPreview"
      :href="!isMapPostPreview ? attachmentUrlForDisplay : null"
      target="_blank"
      rel="noopener nofollow"
      :class="{ 'beethoven-preview': true, 'is-fallback': isFallback }"
      :data-category="contentCategory"
      :data-subcategory="contentSubcategory"
      :download="downloadFileName"
      draggable="false"
      :data-luminance="previewImageLuminance"
      @click="play"
    >
      <base-image
        draggable="false"
        loading="lazy"
        :alt="title"
        :class="{ 'beethoven-preview-image': true, 'is-tiny': isTiny }"
        :src="previewImageSrc"
        :post-width="postWidth"
        :post-height="postHeight"
        :width="previewImageWidth"
        :height="previewImageHeight"
        :background-color="previewImageColor"
        :data-aspect-ratio="previewImageAspectRatio"
      />
      <div v-if="showMeta" class="beethoven-preview-meta" :style="{ 'background-color': previewImageColor }">
        <div class="beethoven-title" :title="title">{{ title }}</div>
        <div v-if="description" class="beethoven-description" :title="description">{{ description }}</div>
        <div v-if="providerName" class="beethoven-provider">
          <div class="beethoven-provider-name caption-type">{{ providerName }}</div>
        </div>
      </div>
      <i v-if="contentCategory == 'video'" class="beethoven-video-play-action beethoven-action" />
      <i v-if="contentCategory == 'audio'" class="beethoven-audio-play-action beethoven-action" />
      <i v-if="contentCategory == 'file'" class="beethoven-file-extension">{{ extension }}</i>
    </a>
    <div v-if="showEmbed" class="beethoven-inline-embed">
      <div
        class="beethoven-inline-embed-container"
        :style="{ 'background-color': previewImageColor }"
        :data-aspect-ratio="previewImageAspectRatio"
        v-html="embedHtml"
      />
      <div
        class="beethoven-inline-embed-actions"
        :style="{ 'background-color': previewImageColor, height: embedMetaHeight + 'px' }"
        :data-luminance="previewImageLuminance"
      >
        <div class="button-list">
          <button class="icon-button" :title="__('Back to preview')" @click="backToPreview">
            <base-icon name="arrow_back" />
          </button>
        </div>
        <div class="button-list">
          <button class="icon-button" :title="__('Zoom')" @click="zoom">
            <base-icon name="zoom_in" />
          </button>
          <a
            v-if="isDownloadable"
            target="_blank"
            :download="title"
            :href="downloadUrl"
            class="icon-button"
            :title="__('Download')"
            draggable="false"
          >
            <base-icon name="download" />
          </a>
          <button class="icon-button" :title="__('Open in new window')" @click="openInNew">
            <base-icon name="open_in_new" />
          </button>
        </div>
      </div>
    </div>
    <div v-if="errorText" class="error-text">
      {{ errorText }}
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapState, mapGetters, mapActions } from 'vuex'
import { $ } from 'bits/jquery'
import BaseImage from 'vuecomponents/base_image'
import BaseIcon from 'vuecomponents/base_icon'
import ProgressIndeterminate from 'vuecomponents/progress_indeterminate'
import { hyphenatedCurrentLocale as locale } from 'bits/current_locale'
import { getDisplayAttributes } from 'bits/beethoven'
import { downloadUrl } from 'bits/attachments'

function getStylizedProviderName(name) {
  if (!name) return ''
  // This is done to comply with Youtube's requirements in order to use their player on Padlet.
  if (name.toLowerCase() === 'youtube') return name
  return name.toLowerCase()
}

export default {
  components: {
    BaseIcon,
    BaseImage,
    ProgressIndeterminate,
  },
  props: ['post', 'url', 'width', 'height', 'actionable', 'isMapPostPreview'],
  data() {
    return {
      canBeActioned: this.actionable !== 'no',
      showPreview: true,
      showEmbed: false,
      link: null,
      isLoaded: null,
      isTiny: null,
      isFallback: null,
      previewImageSrc: null,
      previewImageWidth: null,
      previewImageHeight: null,
      previewImageColor: null,
      previewImageAspectRatio: null,
      previewImageLuminance: null,
      showMeta: null,
      title: null,
      downloadFileName: null,
      description: null,
      providerName: null,
      contentCategory: null,
      contentSubcategory: null,
      extension: null,
      embedCode: null,
      isDownloadable: null,
      showHtml: false,
      embedHtml: null,
      embedMetaHeight: null,
      errorText: null,
    }
  },
  computed: {
    ...mapState(['dpr', 'isApp', 'isMobile', 'isPhone', 'canPostMessage']),
    ...mapState('postAction', ['currentlyPlayingPostCid', 'zoomedPostCid']),
    ...mapGetters(['useArvo']),
    ...mapGetters('post', ['isInitialPaginatedDataLoadDone']),
    attachmentUrlForDisplay() {
      // Note: This should be used in display/href/... instead of this.url so if the attachment is censored (e.g. unsafe links), the bad attachment won't be exposed
      return this.link?.url
    },
    postWidth() {
      return this.width
    },
    postHeight() {
      return Math.floor(this.width / this.previewImageAspectRatio)
    },
    downloadUrl() {
      return downloadUrl(this.attachmentUrlForDisplay)
    },
  },
  watch: {
    width(newValue) {
      // if this.previewImageWidth === null, it means the component is loaded for the first time,
      // getBeethovenAttributesOnNextTick should have already been called in mounted
      const isFirstLoad = this.previewImageWidth === null
      if (!isFirstLoad && newValue > this.previewImageWidth) {
        this.getBeethovenAttributesOnNextTick()
      }
    },
    currentlyPlayingPostCid(newCid) {
      if (!this.showPreview && newCid && this.post.cid !== newCid) {
        this.backToPreview()
      }
    },
  },
  mounted() {
    // if initial wish load is done, fetch links endpoint, else wait for it to be done first
    if (this.isInitialPaginatedDataLoadDone) {
      this.getBeethovenAttributesOnNextTick()
    } else {
      this.unwatchIsInitialPaginatedDataLoadDone = this.$watch('isInitialPaginatedDataLoadDone', (newValue) => {
        if (newValue) {
          this.getBeethovenAttributesOnNextTick()
          this.unwatchIsInitialPaginatedDataLoadDone()
        }
      })
    }
  },
  beforeDestroy() {
    this.unsetAsCurrentlyPlayingPost()
  },
  methods: {
    ...mapActions('postAction', ['expandPost', 'zoomPost', 'setCurrentlyPlayingPost', 'unsetCurrentlyPlayingPost']),
    ...mapActions('nativeApp', ['openLink']),
    ...mapActions('attachment', ['updateLinkDisplayAttributes']),
    addQueryParams(url, paramsString) {
      if (url.match(/\?/)) {
        return [url, paramsString].join('&')
      }
      return [url, paramsString].join('?')
    },
    playInApp() {
      let linkType = 'external'
      if (this.contentSubcategory === 'padlet') {
        linkType = 'padlet'
      } else if (this.contentCategory === 'photo' || this.contentCategory === 'file') {
        linkType = 'internal_mediaplayer'
      } else if (this.contentCategory === 'page' || this.contentCategory === 'document') {
        linkType = 'internal_webview'
      }

      this.openLink({
        url: this.attachmentUrlForDisplay,
        linkType,
        contentCategory: this.contentCategory,
        contentSubcategory: this.contentSubcategory,
      })
    },
    unsetAsCurrentlyPlayingPost() {
      if (this.currentlyPlayingPostCid === this.post.cid) {
        this.unsetCurrentlyPlayingPost({ cid: this.post.cid })
      }
    },
    play(e) {
      if (!this.canBeActioned) {
        // opening link: cant be actioned
        return false
      }
      if ($(this.$el).parents('.noclick,.is-dragging').length > 0) {
        // opening link: dragging
        e.preventDefault()
        return false
      }
      if (this.contentCategory === 'video' || this.contentCategory === 'audio') {
        // opening link: content is video and audio so we display player
        this.embedInline()
        e.preventDefault()
        return false
      }
      if (this.isApp && this.canPostMessage) {
        // opening link: isApp so use postMessage to open link
        this.playInApp()
        return false
      }
      if (this.isPhone) {
        // opening link: on phone, only images zoom
        if (this.contentCategory === 'photo') {
          this.zoom()
          e.preventDefault()
          return false
        }
      } else if (this.embedCode && this.contentSubcategory !== 'padlet') {
        // opening link: display preview for file type having the embed code
        this.zoom()
        e.preventDefault()
        return false
      }
      // opening link: download or go to url
      return true
    },
    embedInline() {
      this.setCurrentlyPlayingPost({ cid: this.post.cid })
      this.showPreview = false
      this.showEmbed = true
      this.embedHtml = this.embedCode
      const $embed = $(this.embedCode)

      if ($embed.is('iframe')) {
        $embed
          .attr({
            width: this.postWidth || this.previewImageWidth,
            height: this.postHeight || this.previewImageHeight,
          })
          .css('background', this.previewImageColor)
        const currentSrc = $embed.attr('src')
        let newSrc = this.addQueryParams(currentSrc, 'autoplay=1')
        if (this.providerName.toLowerCase() === 'youtube') {
          newSrc = this.addQueryParams(newSrc, 'rel=0')
        }
        $embed.attr('src', newSrc)
        this.embedHtml = $embed[0].outerHTML
        this.embedMetaHeight = $('.beethoven-preview-meta', this.$el).height()
      }
      $(this.$el).parents('.wish').addClass('is-playing')
    },
    zoom() {
      this.backToPreview()
      this.zoomPost({
        zoomedPostCid: this.post ? this.post.cid : null,
        zoomedLink: this.link,
      })
    },
    backToPreview() {
      this.showPreview = true
      this.showEmbed = false
      this.unsetAsCurrentlyPlayingPost()
      return $(this.$el).parents('.wish').removeClass('is-playing')
    },

    expandPost() {
      this.backToPreview()
      this.expandPost({ postCid: this.post.cid })
    },
    openInNew() {
      if (this.isApp) {
        this.openLink({
          linkType: 'external',
          url: this.attachmentUrlForDisplay,
          contentCategory: this.contentCategory,
          contentSubcategory: this.contentSubcategory,
        })
      } else {
        window.open(this.attachmentUrlForDisplay)
      }
    },
    async getBeethovenAttributes() {
      const bhOptions = {
        dpr: this.dpr,
        locale: locale,
        width: this.width,
        height: this.height,
        updated_at: this.updatedAt,
        useArvo: this.useArvo,
      }

      const data = await getDisplayAttributes(this.url, bhOptions)
      if (data.error) {
        this.errorText = data.error
        return
      }
      if (data === null) {
        this.errorText = this.__('Oops! Something went wrong. Please try again.')
        return
      }
      this.updateLinkDisplayAttributes({ url: this.url, attributes: data })
      this.link = data
      this.isLoaded = true
      this.isTiny = data.is_tiny
      this.isFallback = data.is_fallback
      this.previewImageSrc = data.image_url
      this.previewImageWidth = data.image_width
      this.previewImageHeight = data.image_height
      this.previewImageColor = data.image_color
      this.previewImageAspectRatio = data.image_aspect_ratio
      this.previewImageLuminance = data.image_luminance
      this.showMeta = data.show_meta
      this.title = data.title
      this.description = data.description
      this.providerName = getStylizedProviderName(data.provider_name)
      this.contentCategory = data.content_category
      this.contentSubcategory = data.content_subcategory
      this.extension = data.extension
      this.embedCode = data.embed_code
      this.isDownloadable = data.is_downloadable && !this.isMobile
      if (this.contentCategory === 'file') {
        this.downloadFileName = this.title
      } else {
        this.downloadFileName = false
      }
    },
    getBeethovenAttributesOnNextTick() {
      this.$nextTick(() => {
        this.getBeethovenAttributes()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$photo-color: #ff4081;
$audio-color: #7c77b9;
$video-color: #fec006;
$document-color: #7fc136;
$page-color: #4a90e2;
$file-color: #7b8693;
$title-color: #283238;
$description-color: #47525d;
$provider-color: #828990;

@import './../../assets/stylesheets/3/modules/all';

.surface-beethoven-preview {
  display: block;

  .progress-container {
    padding: 16px;
  }
}

.beethoven-preview {
  font-family: $font-family;
  position: relative;
  display: block;
  color: var(--body-text-color);
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  user-select: none;

  &:hover,
  &:visited,
  &:active,
  &:hover:visited {
    color: var(--body-text-color);
  }
}

.beethoven-preview-image {
  -webkit-touch-callout: none !important;
  background-size: 200px 200px;
  background-color: white;
  display: block;
  margin: 0px auto;
  max-width: 100%;
}

.beethoven-preview-image.beethoven-success:not(.is-tiny) {
  background-color: white !important;
}

.beethoven-fallback-icon {
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 50%;
  height: 72px;
  left: calc(50% - 36px);
  position: absolute;
  top: calc(50% - 36px);
  width: 72px;
}

// --------------------------------------------------------
// METADATA SECTION
// --------------------------------------------------------
.beethoven-preview-meta {
  padding: 8px 12px;
  min-height: 40px;
  text-align: left;
  background: $superlight-grey;
}

.beethoven-title {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.1px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.beethoven-description {
  font-size: 12px;
  font-weight: normal;
  letter-spacing: 0.1px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.beethoven-provider-name {
  font-size: 11px;
  font-weight: normal;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.beethoven-preview:hover {
  .beethoven-preview-curtain {
    opacity: 1;
  }

  .beethoven-preview-meta {
    opacity: 1;
  }
}

.beethoven-video-symbol-small {
  background-color: #ffd300;
  background-image: url('./../../assets/images/video_symbol.svg');
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: 50%;
  display: none;
  flex-shrink: 0;
  height: 8px;
  padding: 8px 7px;
  width: 10px;
}

.beethoven-audio-symbol-small {
  background-color: $audio-color;
  background-image: url('./../../assets/images/audio_symbol.svg');
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: 50%;
  display: none;
  flex-shrink: 0;
  height: 11px;
  padding: 6px 8px 7px 8px;
  width: 8px;
}

.beethoven-document-symbol-small {
  background-color: #7fc136;
  background-image: url('./../../assets/images/document_symbol.svg');
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: 50%;
  display: none;
  flex-shrink: 0;
  height: 13px;
  padding: 5px 6px 6px 8px;
  width: 10px;
}

.beethoven-generic-attachment {
}

// --------------------------------------------------------
// PHOTO
// --------------------------------------------------------
[data-category='photo'] {
  .beethoven-fail,
  &.is-fallback .beethoven-preview-image {
    background-image: url('./../../assets/images/3/fallback_image.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }
}

// --------------------------------------------------------
// VIDEO
// --------------------------------------------------------
[data-category='video'] {
  .beethoven-fail,
  &.is-fallback .beethoven-preview-image {
    background-image: url('./../../assets/images/3/fallback_video.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }

  .beethoven-video-play-action {
    background-image: url('./../../assets/images/play_action.png');
    background-repeat: no-repeat;
    background-size: contain;
    display: block;
    height: 28px;
    left: 10px;
    position: absolute;
    top: 10px;
    width: 28px;
  }
}

// --------------------------------------------------------
// AUDIO
// --------------------------------------------------------
[data-category='audio'] {
  .beethoven-fail,
  &.is-fallback .beethoven-preview-image {
    background-image: url('./../../assets/images/3/fallback_audio.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }

  .beethoven-audio-play-action {
    background-image: url('./../../assets/images/play_action.png');
    background-repeat: no-repeat;
    background-size: contain;
    display: block;
    height: 28px;
    left: 10px;
    position: absolute;
    top: 10px;
    width: 28px;
  }
}

// --------------------------------------------------------
// DOCUMENT
// --------------------------------------------------------
[data-category='document'] {
  .beethoven-fail,
  &.is-fallback .beethoven-preview-image {
    background-image: url('./../../assets/images/3/fallback_document.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }
}

[data-subcategory='spreadsheet'] {
  .beethoven-fail,
  &.is-fallback .beethoven-preview-image {
    background-image: url('./../../assets/images/3/fallback_spreadsheet.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }
}

[data-subcategory='presentation'] {
  .beethoven-fail,
  &.is-fallback .beethoven-preview-image {
    background-image: url('./../../assets/images/3/fallback_presentation.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }
}

// --------------------------------------------------------
// PAGE
// --------------------------------------------------------
[data-category='page'] {
  .beethoven-fail,
  &.is-fallback .beethoven-preview-image {
    background-image: url('./../../assets/images/3/fallback_link.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }
}

// --------------------------------------------------------
// FALLBACK
// --------------------------------------------------------
[data-category='unknown'],
[data-category='file'] {
  .beethoven-preview-image {
    background-image: url('./../../assets/images/3/fallback_file.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  }

  .beethoven-file-extension {
    display: block;
    position: absolute;
    color: $ultralight-grey;
    text-transform: uppercase;
    right: 16px;
    top: 8px;
    width: calc(100% - 20px);
    overflow: hidden;
    font-size: 24px;
    letter-spacing: 2px;
    font-weight: 700;
    text-align: right;
    text-shadow: -1px -1px 0 #d3d3d3, 1px -1px 0 #d3d3d3, -1px 1px 0 #d3d3d3, 1px 1px 0 #d3d3d3;
  }
}

// --------------------------------------------------------
// INLINE EMBED
// --------------------------------------------------------
.beethoven-inline-embed {
  position: relative;
  overflow: hidden;

  iframe {
    display: block;
  }
}
.beethoven-inline-embed-actions {
  padding: 8px;
  display: flex;
  align-items: flex-end;
  min-height: 40px;
  justify-content: space-between;
}
.beethoven-inline-embed-container {
  position: relative;
  overflow: hidden;
}

// --------------------------------------------------------
// MINI PREVIEW
// --------------------------------------------------------

.surface-beethoven-preview.map-preview {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(#000, 0.07);
  .progress-container {
    width: 100%;
  }
  .beethoven-preview {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    .beethoven-preview-meta {
      display: none;
    }
    .beethoven-preview-image {
      object-fit: cover;
      max-height: 100%;
    }
    .beethoven-action {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
}

.error-text {
  color: $error-color;
  font-size: smaller;
  margin: 8px;
}
</style>
